/**
 * Modal for viewing and editing POC assignment group metadata
 * (`/configurations/pharmacy_order_updates/metadata/poc_assignment_groups`).
 */

import { useMemo } from "react";
import { closeModal, Modal, TextInput } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import MultiSelectTagInput from "../../../../../components/multiSelectTagInut/MultiSelectTagInput";
import { ModalId } from "../../../../../enums/modalId";
import { Delete } from "../../../../../svg/delete";
import EditPencilIcon from "../../../../../svg/editPencil";
import { useFilenameBatchOptions } from "../hooks/useFilenameBatchOptions";
import { usePocAssignmentGroupsMetadataModal } from "../hooks/usePocAssignmentGroupsMetadataModal";

interface UpdateGroupModalProps {
  /** Invoked after a successful save, before the modal closes (e.g. refetch parent caches). */
  onGroupsSaved?: () => void;
}

/**
 * Renders the Groups editor modal backed by Firestore assignment-group metadata.
 *
 * @param onGroupsSaved - Optional callback after a successful Firestore save.
 */
const UpdateGroupModal = ({ onGroupsSaved }: UpdateGroupModalProps) => {
  const filenameBatchOptions = useFilenameBatchOptions();
  const {
    rows,
    loading,
    saving,
    loadError,
    isDirty,
    editingKey,
    setEditingKey,
    removeRow,
    updateRow,
    save,
    dismissRedux,
    resetLocal,
  } = usePocAssignmentGroupsMetadataModal();

  const batchSelectOptions = useMemo(
    () =>
      filenameBatchOptions.options.map((batch) => ({
        value: batch,
        label: batch,
      })),
    [filenameBatchOptions.options],
  );

  /** Discards unsaved edits and closes (Cancel / X). */
  const handleDiscardAndClose = () => {
    closeModal(ModalId.UPDATE_GROUP_MODAL);
    dismissRedux();
    resetLocal();
  };

  const handleSave = async () => {
    const ok = await save();
    if (ok) {
      onGroupsSaved?.();
      closeModal(ModalId.UPDATE_GROUP_MODAL);
      dismissRedux();
      resetLocal();
    }
  };

  return (
    <Modal
      dialogId={ModalId.UPDATE_GROUP_MODAL}
      title="Groups"
      onClose={handleDiscardAndClose}
      onCancel={handleDiscardAndClose}
      onSave={handleSave}
      saveButtonText={saving ? "Saving…" : "Save"}
      cancelText="Cancel"
      disableSave={!isDirty || saving || loading}
      autoCloseOnSave={false}
    >
      <div className="flex max-h-[70vh] min-h-[200px] flex-col gap-3 overflow-y-auto py-1">
        {loading && (
          <div className="py-8">
            <LoaderMessage message="Loading groups…" />
          </div>
        )}
        {!loading && loadError != null && (
          <div className="text-small text-red-600">{loadError}</div>
        )}
        {!loading && loadError == null && (
          <>
            <div className="flex flex-col gap-3">
              {rows.map((row) => {
                const isEditing = editingKey === row.key;
                const selectedBatchOptions = row.batches.map((batch) => {
                  const fromList = batchSelectOptions.find(
                    (o) => o.value === batch,
                  );
                  return fromList ?? { value: batch, label: batch };
                });
                return (
                  <div
                    key={row.key}
                    className="flex flex-wrap items-start gap-2 sm:items-center"
                  >
                    {isEditing ? (
                      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:max-w-md">
                        <TextInput
                          id={`group-name-${row.key}`}
                          label=""
                          placeholder="Group name"
                          defaultValue={row.group_name}
                          onChange={(e) =>
                            updateRow(row.key, { group_name: e.value ?? "" })
                          }
                          onBlur={(e) =>
                            updateRow(row.key, { group_name: e.value ?? "" })
                          }
                        />
                        <div className="flex flex-col gap-1">
                          <span className="text-tiny font-medium text-primaryGray-8">
                            Batches
                          </span>
                          <MultiSelectTagInput
                            id={`group-batches-${row.key}`}
                            options={batchSelectOptions}
                            selectedOptions={selectedBatchOptions}
                            placeholder={
                              filenameBatchOptions.loading
                                ? "Loading batches…"
                                : "Select batch files"
                            }
                            onChange={(values) =>
                              updateRow(row.key, { batches: values })
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex min-h-[40px] min-w-0 flex-1 items-center">
                        <div className="rounded-md border border-primaryGray-1 px-3 py-1.5 text-sm text-primaryGray-1">
                          {row.group_name.trim() !== ""
                            ? row.group_name
                            : "Untitled group"}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-shrink-0 items-center gap-2 pt-1">
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-primaryGray-16"
                        aria-label={isEditing ? "Done editing" : "Edit group"}
                        onClick={() =>
                          setEditingKey(isEditing ? null : row.key)
                        }
                      >
                        <EditPencilIcon
                          stroke="#2563eb"
                          width={20}
                          height={20}
                        />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-primaryGray-16"
                        aria-label="Delete group"
                        onClick={() => removeRow(row.key)}
                      >
                        <Delete width={20} height={20} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UpdateGroupModal;
